import { useLocale } from '@solinkcorp/locale';
import { Button } from '@solinkcorp/solink-ui/components/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@solinkcorp/solink-ui/components/DropdownMenu';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
} from '@solinkcorp/solink-ui/components/ui/sidebar';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { ChevronDown, GripVertical, PanelRightClose, Plus } from 'lucide-react';
import { type CSSProperties, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { getDayPartAnalytics } from '../../analytics/sdmAdoptionMetrics';
import {
  DAY_PART_SIDEBAR_MAX_WIDTH,
  DAY_PART_SIDEBAR_MIN_WIDTH,
  DAY_PART_WEEK_INDEX,
  SDM_EVENTS,
} from '../../constants';
import { useSDMAnalytics } from '../../contexts/AnalyticsContext';
import type { ServiceDisplayDayPart } from '../../day-part/utils';
import { useDayPartsLocale } from '../../hooks/useDayPartLocale';
import { useResizePx } from '../../hooks/useResizePx';
import {
  useDeleteServiceDisplayDayPart,
  useServiceDisplayDayParts,
} from '../../hooks/useServiceDisplayDayParts';
import {
  getAssignedDayPartIdsFromAllLayouts,
  getDayPartIdToLayoutNames,
} from '../../layout/layoutWidgetLookup';
import type { ServiceDisplayLayoutSnapshot } from '../../layout/types';
import { DayPartDialog } from './DayPartDialog/DayPartDialog';
import { DayPartScheduleView } from './DayPartScheduleView';

interface DayPartSidebarProps {
  open: boolean;
  toggleSidebar: () => void;
  dayPartIndex: number;
  onDayPartIndexChange: (index: number) => void;
  sidebarWidth: number;
  onSidebarWidthChange: (width: number) => void;
  /** Readonly array of layouts to check day part schedule conflicts across all layout configurations */
  layouts?: ReadonlyArray<ServiceDisplayLayoutSnapshot>;
}

export const DayPartSidebar = ({
  open,
  toggleSidebar,
  dayPartIndex,
  onDayPartIndexChange,
  sidebarWidth,
  onSidebarWidthChange,
  layouts = [],
}: DayPartSidebarProps) => {
  const { data: dayPartData } = useServiceDisplayDayParts();
  const deleteDayPart = useDeleteServiceDisplayDayPart();
  const analytics = useSDMAnalytics();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDayPartId, setEditingDayPartId] = useState<string | null>(null);
  const assignedDayPartIds = useMemo(() => getAssignedDayPartIdsFromAllLayouts(layouts), [layouts]);
  const dayPartIdToLayoutNames = useMemo(() => getDayPartIdToLayoutNames(layouts), [layouts]);
  const dayPartsAssignedToWidgets = useMemo(
    () => (dayPartData ?? []).filter((dp: ServiceDisplayDayPart) => assignedDayPartIds.has(dp.id)),
    [dayPartData, assignedDayPartIds],
  );
  const editingDayPart = editingDayPartId
    ? dayPartData.find((dp) => dp.id === editingDayPartId)
    : undefined;
  const locale = useLocale();
  const dayParts = useDayPartsLocale();

  const isWeekView = dayPartIndex === DAY_PART_WEEK_INDEX;

  const { resizeRef } = useResizePx({
    width: sidebarWidth,
    onWidthChange: onSidebarWidthChange,
    minWidth: DAY_PART_SIDEBAR_MIN_WIDTH,
    maxWidth: DAY_PART_SIDEBAR_MAX_WIDTH,
  });

  const navigateDayPart = (offset: number) => {
    const newIndex = dayPartIndex + offset;
    // Skip week view (index 7) in nav options
    const pos =
      newIndex === dayParts.length - 1 ? 0 : newIndex === -1 ? dayParts.length - 2 : newIndex;
    onDayPartIndexChange(pos);
  };

  return (
    <Sidebar
      side="right"
      collapsible="offcanvas"
      className="h-full border border-border-muted bg-background-surface p-3"
      // The Day Parts panel is a content surface, not the app nav rail. Align the
      // solink-ui `Sidebar` inner panel (`bg-sidebar`) with the chrome color
      // (`background-surface`). The two tokens match in light mode but diverge in
      // dark mode (`--sidebar` -> Gray-950 vs `--background-surface` -> Gray-900),
      // which made the dark panel look off. Scoped to this Sidebar only.
      style={{ ['--sidebar']: 'var(--background-surface)' } as CSSProperties}
    >
      {open && (
        <div
          ref={resizeRef}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          className="group/resize absolute inset-y-0 -left-[5px] z-50 flex w-2 cursor-col-resize items-center justify-center transition-colors hover:bg-primary/10 active:bg-primary/20"
        >
          <div className="flex h-8 w-3 items-center justify-center rounded-sm border border-transparent bg-border text-muted-foreground transition-colors group-hover/resize:bg-primary group-hover/resize:text-primary-foreground group-hover/resize:border-primary/30 group-active/resize:bg-primary/80">
            <GripVertical size={10} />
          </div>
        </div>
      )}
      <SidebarHeader>
        <div className="m-auto inline-flex w-full flex-row items-center justify-between">
          <Button
            variant={open ? 'ghost' : 'default'}
            size="icon"
            onClick={toggleSidebar}
            className={cn(!open && 'ml-3')}
          >
            <PanelRightClose size={16} />
          </Button>

          <span className="align-middle heading-lg">
            {locale.serviceDisplayMonitor.dayPartsTitle}
          </span>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setEditingDayPartId(null);
              setDialogOpen(true);
            }}
          >
            <Plus size={16} />
          </Button>
        </div>

        <div
          className={cn(
            open ? 'block' : 'invisible',
            'flex flex-row items-center justify-center gap-2',
          )}
        >
          <SidebarMenu>
            <SidebarMenuItem className="m-auto">
              <DropdownMenu>
                <DropdownMenuTrigger className="flex min-h-[36px] min-w-[204px] items-center gap-2 rounded-md border border-r-[2px] border-border p-2 text-sm hover:bg-accent">
                  <span className="flex-1 align-middle body-md-strong">
                    {isWeekView
                      ? locale.serviceDisplayMonitor.dayPart.week
                      : locale.serviceDisplayMonitor.dayPart.day}
                  </span>
                  <ChevronDown size={16} className="shrink-0" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[--radix-popper-anchor-width] border-border-muted bg-background-surface">
                  <DropdownMenuItem onClick={() => onDayPartIndexChange(0)}>
                    {locale.serviceDisplayMonitor.dayPart.day}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDayPartIndexChange(DAY_PART_WEEK_INDEX)}>
                    {locale.serviceDisplayMonitor.dayPart.week}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </div>
      </SidebarHeader>

      <SidebarContent className={cn(open ? 'block' : 'invisible')}>
        <DayPartScheduleView
          dayPartIndex={dayPartIndex}
          sidebarOpen={open}
          dayPartData={dayPartData}
          onEdit={(id) => {
            setEditingDayPartId(id);
            setDialogOpen(true);
          }}
          onNavigateDay={navigateDayPart}
          onDeleteDayPart={async (id) => {
            const deleted = (dayPartData ?? []).find((dp: ServiceDisplayDayPart) => dp.id === id);
            const result = await deleteDayPart(id);
            if (result instanceof Error) {
              toast.error(locale.serviceDisplayMonitor.failedToDeleteDayPart, {
                position: 'bottom-right',
              });
            } else {
              analytics.trackSDMEvent(SDM_EVENTS.DELETE_DAY_PART, {
                description: 'Deleted a service display monitor day part',
                ...getDayPartAnalytics(deleted ?? { id }),
                existingDayPartCount: Math.max(0, (dayPartData ?? []).length - 1),
                wasAssignedToWidget: assignedDayPartIds.has(id),
              });
            }
            setEditingDayPartId(null);
          }}
        />
      </SidebarContent>
      <DayPartDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        existingDayParts={(dayPartData ?? []) as ServiceDisplayDayPart[]}
        dayPart={editingDayPart as ServiceDisplayDayPart | undefined}
        dayPartsAssignedToWidgets={dayPartsAssignedToWidgets as ServiceDisplayDayPart[]}
        dayPartIdToLayoutNames={dayPartIdToLayoutNames}
      />
    </Sidebar>
  );
};
